/**
 * tiers.ts — Canonical ɳSentry tier/quota table
 *
 * Purpose:    Single source of truth for what each SaaS tier includes (monitor
 *             limits, retention, alert channels, API access, seats). Consumed
 *             by MonitorsPage/StatusPagesPage/OverviewPage/ApiKeysPage/BillingPage
 *             to render quota meters and gate actions, and by saas-mock.ts to
 *             enforce quotas server-side (mock).
 * Inputs:     none (static table)
 * Outputs:    TIER_IDS, TierQuota, TIERS record
 * Constraints: Prices are NEVER defined here — see ./pricing.ts (SPORT-driven).
 *              tier id mapping to pricing.ts PRICING_TIERS ids: free→free,
 *              sentry-bundle→nsentry, nself-plus→plus.
 * SOT:        nsentry SaaS plan §3 (tier/quota spec, task-provided authoritative values)
 */

export type TierId = 'free' | 'sentry-bundle' | 'nself-plus'

export const TIER_IDS: TierId[] = ['free', 'sentry-bundle', 'nself-plus']

export type AlertChannel = 'email' | 'webhook' | 'telegram' | 'slack' | 'oncall'
export type ApiAccessLevel = 'read-only' | 'full'

export interface TierQuota {
  id: TierId
  label: string
  /** Maps to pricing.ts PRICING_TIERS[].id for price lookups */
  pricingId: string
  monitors: { max: number; minIntervalSeconds: number }
  statusPages: number
  retentionDays: number
  errorEventsPerMonth: number
  rumPageviewsPerMonth: number
  heartbeats: number
  alertChannels: AlertChannel[]
  apiAccess: ApiAccessLevel
  seats: number
}

export const TIERS: Record<TierId, TierQuota> = {
  free: {
    id: 'free',
    label: 'Free',
    pricingId: 'free',
    monitors: { max: 10, minIntervalSeconds: 300 },
    statusPages: 1,
    retentionDays: 7,
    errorEventsPerMonth: 5_000,
    rumPageviewsPerMonth: 10_000,
    heartbeats: 3,
    alertChannels: ['email'],
    apiAccess: 'read-only',
    seats: 1,
  },
  'sentry-bundle': {
    id: 'sentry-bundle',
    label: 'ɳSentry Bundle',
    pricingId: 'nsentry',
    monitors: { max: 50, minIntervalSeconds: 60 },
    statusPages: 3,
    retentionDays: 90,
    errorEventsPerMonth: 100_000,
    rumPageviewsPerMonth: 250_000,
    heartbeats: 25,
    alertChannels: ['email', 'webhook', 'telegram', 'slack'],
    apiAccess: 'full',
    seats: 3,
  },
  'nself-plus': {
    id: 'nself-plus',
    label: 'ɳSelf+',
    pricingId: 'plus',
    monitors: { max: 100, minIntervalSeconds: 30 },
    statusPages: 10,
    retentionDays: 365,
    errorEventsPerMonth: 1_000_000,
    rumPageviewsPerMonth: 2_000_000,
    heartbeats: 100,
    alertChannels: ['email', 'webhook', 'telegram', 'slack', 'oncall'],
    apiAccess: 'full',
    seats: 10,
  },
}

/** Monitor check-interval options (minutes) allowed at or above a tier's floor. */
const ALL_INTERVAL_MINUTES = [1, 5, 10, 15, 30, 60] as const

export function allowedIntervalMinutes(tier: TierId): number[] {
  const floorSeconds = TIERS[tier].monitors.minIntervalSeconds
  return ALL_INTERVAL_MINUTES.filter((m) => m * 60 >= floorSeconds)
}

export function usagePct(used: number, max: number): number {
  if (max <= 0) return 0
  return Math.min(100, Math.round((used / max) * 100))
}
